import type { IRestUiApiClientOptions } from '../../types'
import type { FetchApiClient } from '../fetch-api-client'

export type { IRestUiApiClientOptions }

export abstract class UiApiGetClientBaseV1<Item> {
  protected readonly restClient: FetchApiClient
  protected readonly session: { tenantId: string; userId: string }

  constructor({ restClient, session }: IRestUiApiClientOptions) {
    this.restClient = restClient
    this.session = session
  }

  protected async restGet<T>(
    path: string,
    queryParams?: Record<string, string>,
  ): Promise<T> {
    return this.restClient.get<T>(
      path,
      {},
      queryParams ? { queryParams } : undefined,
    )
  }

  protected async restPost<T>(path: string, body: unknown): Promise<T> {
    return this.restClient.post<T>(path, {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  }

  protected async restPut<T>(path: string, body: unknown): Promise<T> {
    return this.restClient.put<T>(path, {
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    })
  }

  protected async restDelete(path: string): Promise<void> {
    const response = await this.restClient.fetch(path, { method: 'DELETE' })
    if (!response.ok) {
      const body = await response.text().catch(() => '')
      throw new Error(`DELETE ${path} failed: ${response.status} ${body}`)
    }
  }

  protected buildQueryParams(
    params: Record<string, unknown>,
  ): Record<string, string> {
    const result: Record<string, string> = {}
    for (const [key, value] of Object.entries(params)) {
      if (value === undefined || value === null) continue
      if (typeof value === 'object' && value !== null && 'value' in value) {
        result[key] = String((value as { value: unknown }).value)
      } else {
        result[key] = String(value)
      }
    }
    return result
  }

  public abstract get(request: unknown): Promise<Item | null>
}
